import { useEffect, useState } from 'react';

import {
  ArrowButton, Button, ColumnSortEnum, Modal, Select, Table, TableMetadata,
  TableStateWithFilters,
  TextField, Typography, useTable
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as AddIcon } from "@king-ict/eupisi-ui/components/Icon/Add.svg";
import { ReactComponent as DownloadIcon } from "@king-ict/eupisi-ui/components/Icon/Download.svg";
import { ExportTypeEnum } from '@king-ict/eupisi-ui/models';
import { codeBookToSelectData, nameof } from '@king-ict/eupisi-ui/utils';
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { deleteProgram, getPrograms, exportPrograms } from '../../api/nisooApi';
import { useDidUpdateEffect, useInput } from "../../hooks";
import { ProgramSort } from '../../models/CodeBook';
import { ProgramCodeBookExport } from '../../models/CodeBook/ProgramCodeBookExport';
import { ProgramCodeBookSearch, programCodeBookSearchInitial } from '../../models/CodeBook/ProgramCodeBookSearch';
import { ProgramCodeBook } from '../../models/Program';
import { SortDirection } from '../../models/Shared';
import { RoleEnum } from '../../models/User/RoleEnum';
import {
  useGetProgramTypesQuery,
  useGetEducationSectorsQuery
} from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from '../../queries/userQuery';
import { codebookRouteProgramName } from '../../routes/routes';
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookProgramName(): JSX.Element {
  const storageKey = 'CodeBookProgramNameSearchTableState';
  const defaultPageSaze = 10;

  const createInitialTableState = (): TableStateWithFilters<ProgramCodeBookSearch> => {
    const sessionStorageTableState = sessionStorage.getItem(storageKey);
    console.log(sessionStorageTableState);
    if (sessionStorageTableState === null) {
      return {
        name: "",
        programTypeIds: [],
        educationSectorIds: [],
        page: 1,
        pageSize: defaultPageSaze,
        sortBy: 1,
        order: ColumnSortEnum.ASC
      };
    }
    var result = JSON.parse(sessionStorageTableState) as TableStateWithFilters<ProgramCodeBookSearch>;
    return result;
  }

  const createInitialSearch = (initialTableState: TableStateWithFilters<ProgramCodeBookSearch>): ProgramCodeBookSearch => {
    return {
      name: initialTableState.name,
      programTypeIds: initialTableState.programTypeIds,
      educationSectorIds: initialTableState.educationSectorIds
    }
  }

  const history = useHistory();
  const { t } = useTranslation();
  const { handleChange } = useInput();

  const { data: userSession } = useGetUserSessionQuery();
  const { data: programTypes } = useGetProgramTypesQuery();
  const { data: educationSectors } = useGetEducationSectorsQuery();

  const userCanEditData = userSession && (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.Helpdesk || userSession.roleId === RoleEnum.ASOO || userSession.roleId === RoleEnum.ASOOAdmin);

  const [initialTableState] = useState<TableStateWithFilters<ProgramCodeBookSearch>>(() => createInitialTableState());
  const [searchFilter, setSearchFilter] = useState<ProgramCodeBookSearch>(() => createInitialSearch(initialTableState));
  const [tableProgramNames, setTableProgramNames] = useState<ProgramCodeBook[]>([]);

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [programNameToDelete, setProgramNameToDelete] = useState<ProgramCodeBook>()

  const [programNames, setProgramNames] = useState<ProgramCodeBook[]>([]);
  const [itemCount, setItemCount] = useState(programNames?.length ?? 0);

  const callDataFetch = async () => {
    var result = await getPrograms();
    setProgramNames(result);
    setItemCount(result?.length ?? 0);
  }

  const tableMetadata: TableMetadata<ProgramCodeBook> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "name",
        header: t("codeBook.programName"),
        accessor: "name",
        sortBy: 1
      },
      {
        columnKey: "programTypeName",
        header: t("codeBook.programType"),
        accessor: "programTypeName",
        sortBy: 2
      },
      {
        columnKey: "educationSectorName",
        header: t("codeBook.educationSector"),
        accessor: "educationSectorName",
        sortBy: 3
      },
      {
        columnKey: "canDelete",
        header: "",
        accessor: (item) => {
          return (
            <Typography color="primary" className={styles.clearFilters + " ml-3"} onClick={() => deleteItem(item)}>
              {item.canDelete && userSession && (userSession.roleId === RoleEnum.MZOM || userSession.roleId === RoleEnum.MZOMAdmin || userSession.roleId === RoleEnum.Helpdesk) ? t("common.remove") : ""}
            </Typography>
          )
        }
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item) => {
          return (<div className={"text-right"}>
            <ArrowButton ariaLabel={t("codeBook.programNames.goto")}
              onClick={() => { navigateToDetails(item) }}
            />
          </div>)
        }
      }
    ],
    rowToolTip: () => undefined
  };

  const search = async () => {
    setFilters(searchFilter);
  };

  var [tableProps, tableState, setFilters] = useTable<ProgramCodeBookSearch>(
    {
      metadata: tableMetadata,
      data: tableProgramNames,
      pagination: true,
      paginationPage: initialTableState.page as any,
      paginationPageSize: initialTableState.pageSize as any,
      totalItems: itemCount,
      defaultSort: { order: initialTableState.order, sortBy: initialTableState.sortBy }
    }
  );

  const clearFilters = async () => {
    const clearSearchFilter = programCodeBookSearchInitial();
    setSearchFilter(clearSearchFilter);
    sessionStorage.setItem(storageKey, JSON.stringify({
      name: "",
      programTypeIds: [],
      educationSectorIds: [],
      sortBy: tableState.sortBy ?? 1,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? defaultPageSaze
    }));
    setTableProgramNames(programNames);
    setFilters(clearSearchFilter);
  };

  useEffect(() => {
    if (!userSession || (userSession.roleId !== RoleEnum.MZOM && userSession.roleId !== RoleEnum.MZOMAdmin && userSession.roleId !== RoleEnum.Helpdesk && userSession.roleId !== RoleEnum.ASOO && userSession.roleId !== RoleEnum.ASOOAdmin)) {
      return;
    }
    callDataFetch();
  }, [tableState, userSession]);

  useEffect(() => {
    if (searchFilter && (searchFilter.name || (searchFilter.programTypeIds && searchFilter.programTypeIds.length > 0)
      || searchFilter.programTypeIds && searchFilter.educationSectorIds.length > 0)) {
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!programNames) {
      return;
    }
    const filteredProgramNames = () => {
      let filteredPrograms = programNames;
      if (tableState.name) {
        filteredPrograms = filteredPrograms
          .filter((item) => item.name?.toLowerCase()
            .includes(tableState.name.toLowerCase()));
      }
      if (tableState.programTypeIds && tableState.programTypeIds.length > 0) {
        filteredPrograms = filteredPrograms
          .filter((item) => tableState.programTypeIds.includes(item.programTypeId));
      }

      if (tableState.educationSectorIds && tableState.educationSectorIds.length > 0) {
        filteredPrograms = filteredPrograms
          .filter((item) => item.educationSectorId && tableState.educationSectorIds.includes(item.educationSectorId));
      }
      filteredPrograms = [...filteredPrograms];

      const convertNullString = (text: string | null) => {
        if (text)
          return text;
        return "";
      };

      switch (tableState.sortBy) {
        case ProgramSort.programName:
          filteredPrograms
            .sort((x, y) => tableState.order === ColumnSortEnum.ASC ? x.name.localeCompare(y.name) :
              (-1) * x.name.localeCompare(y.name));
          break;
        case ProgramSort.programType:
          filteredPrograms
            .sort((x, y) => tableState.order === ColumnSortEnum.ASC ?
              convertNullString(x.programTypeName).localeCompare(y.programTypeName ?? "") :
              (-1) * convertNullString(x.programTypeName).localeCompare(y.programTypeName ?? ""));
          break;
        case ProgramSort.educationSector:
          filteredPrograms
            .sort((x, y) => tableState.order === ColumnSortEnum.ASC ?
              convertNullString(x.educationSectorName).localeCompare(y.educationSectorName ?? "") :
              (-1) * convertNullString(x.educationSectorName).localeCompare(y.educationSectorName ?? ""));
          break;
      }
      setItemCount(filteredPrograms?.length ?? 0);
      let pageSize = (tableProps.paginationProps?.pageSize ?? 0);
      console.log("tableProps.paginationProps " + tableProps.paginationProps?.page);
      let startIndex = ((tableProps.paginationProps?.page ?? 1) - 1) * pageSize;
      let endIndex = startIndex + pageSize;
      return filteredPrograms.slice(startIndex, endIndex);
    }
    setTableProgramNames(filteredProgramNames());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState, programNames]);

  useDidUpdateEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify({
      ...searchFilter,
      sortBy: tableState.sortBy ?? 1,
      order: tableState.order ?? ColumnSortEnum.ASC,
      page: tableState.page ?? 1,
      pageSize: tableState.pageSize ?? defaultPageSaze
    }));
  }, [tableState])

  const navigateToDetails = (item: ProgramCodeBook) => {
    history.push(
      codebookRouteProgramName(),
      {
        page: "details",
        programName: item
      }
    );
  }

  const deleteItem = async (item: ProgramCodeBook) => {
    if (!item.canDelete) {
      return;
    }

    setProgramNameToDelete(item);
    setOpenConfirmModal(true);
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    await deleteProgram(programNameToDelete!.id!);
    await callDataFetch();
  }

  const navigateToAddNewRecord = () => {
    history.push(
      codebookRouteProgramName(),
      {
        page: "edit"
      }
    );
  };

  const download = async (exportType: ExportTypeEnum) => {
    const convertUserSearchToApiRequest = (programSearch: ProgramCodeBookSearch): ProgramCodeBookExport => {
      return {
        name: programSearch.name,
        programTypeIds: programSearch.programTypeIds,
        educationSectorIds: programSearch.educationSectorIds,
        sortBy: tableState.sortBy ? tableState.sortBy : 0,
        sortDirection: tableState.order && tableState.order === ColumnSortEnum.DESC ? SortDirection.Desc : SortDirection.Asc,
        exportType: exportType
      }
    }
    const searchProgramList = async () => {
      var request = convertUserSearchToApiRequest(searchFilter);
      exportPrograms(request);
    }

    searchProgramList();
  }

  return (
    <>
      <Row className="mt-5">
        <Col md={6}>
          <h3>{t("codeBook.programName")}</h3>
        </Col>
        {userCanEditData &&
          <Col md={6} className="d-flex justify-content-end align-items-center">
            <Button outline icon={<AddIcon />} onClick={navigateToAddNewRecord}>
              {t("common.addNewRecord")}
            </Button>
          </Col>
        }
      </Row>
      <Row className="mt-3">
        <Col md={6}>
          <TextField
            label={t("codeBook.programName")}
            name={nameof<typeof searchFilter>("name")}
            value={searchFilter.name}
            maxLength={80}
            onChange={handleChange(setSearchFilter)}
          />
        </Col>
        <Col md={6}>
          <Select
            multiple
            label={t("request.programType")}
            name={nameof<typeof searchFilter>("programTypeIds")}
            value={searchFilter.programTypeIds}
            onChange={handleChange(setSearchFilter)}
            selectData={codeBookToSelectData(programTypes)}
          />
        </Col>
      </Row>
      <Row className={styles.rowSpacing}>
        <Col md={6}>
          <Select
            multiple
            label={t("codeBook.educationSector")}
            name={nameof<typeof searchFilter>("educationSectorIds")}
            value={searchFilter.educationSectorIds}
            onChange={handleChange(setSearchFilter)}
            selectData={codeBookToSelectData(educationSectors)}
          />
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-center">
          <Button onClick={search}>
            {t("common.search")}
          </Button>
          <Typography color="primary" className={styles.clearFilters + " ml-5"} onClick={clearFilters}>
            {t("common.clearFilters")}
          </Typography>
        </Col>
      </Row>
      <Table  {...tableProps} />
      <Row className="mt-5">
        <Col md={12} className="d-flex justify-content-end align-items-center">
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Csv)}
          >
            {t("common.downloadDocumentExtension", { extension: ".csv" })}
          </Button>
          <Button
            className={styles.exportButton}
            outline
            color={"primary"}
            icon={<DownloadIcon />}
            onClick={() => download(ExportTypeEnum.Excel)}
          >
            {t("common.downloadDocumentExtension", { extension: ".xslx" })}
          </Button>
        </Col>
      </Row>

      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.yesDelete")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
        minWidth={false}
      >
        <div>
          {
            programNameToDelete &&
            <Typography component="h1" variant="largeBold">
              {t("codeBook.programNames.areYouSureDelete", { name: programNameToDelete.name })}
            </Typography>
          }

        </div>
      </Modal>
    </>
  );
}
